import {
  QueryClient,
  dehydrate,
  hashKey,
  type DehydratedState,
  type QueryKey,
} from '@tanstack/react-query';
import { getFunctionName, type FunctionReference } from 'convex/server';
import { convexToJson, type Value } from 'convex/values';

function isConvexValue(value: unknown): value is Value {
  if (value === null) return true;
  switch (typeof value) {
    case 'string':
    case 'number':
    case 'boolean':
    case 'bigint':
      return true;
    case 'object':
      return Array.isArray(value)
        ? value.every(isConvexValue)
        : value instanceof ArrayBuffer ||
            Object.values(value).every(isConvexValue);
    default:
      return false;
  }
}

/**
 * Same hash `ConvexQueryClient.hashFn()` produces for `convexQuery` keys, so
 * server-built entries land on the keys client hooks read.
 */
export function convexQueryHash(queryKey: QueryKey): string {
  const [kind, name, args] = queryKey;
  if (
    kind === 'convexQuery' &&
    typeof name === 'string' &&
    isConvexValue(args)
  ) {
    // `convexQuery` keys already carry the resolved function name.
    return `convexQuery|${name}|${JSON.stringify(convexToJson(args))}`;
  }
  return hashKey(queryKey);
}

export type ServerConvexQueryResult = {
  query: FunctionReference<'query'>;
  args: Record<string, Value>;
  data: unknown;
};

/**
 * Older than any result the client loads itself, so hydrating a snapshot only
 * fills queries without data. A live result that arrived while the server
 * was still building the snapshot is never replaced by the older read.
 */
const SERVER_RESULT_UPDATED_AT = 1;

/** TanStack state for `HydrationBoundary`, keyed like `convexQuery(...)`. */
export function dehydrateConvexQueryResults(
  results: readonly ServerConvexQueryResult[]
): DehydratedState {
  const queryClient = new QueryClient({
    defaultOptions: { queries: { queryKeyHashFn: convexQueryHash } },
  });
  for (const result of results) {
    if (result.data === undefined) continue;
    queryClient.setQueryData(
      ['convexQuery', getFunctionName(result.query), result.args],
      result.data,
      { updatedAt: SERVER_RESULT_UPDATED_AT }
    );
  }
  return dehydrate(queryClient);
}
